import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

interface CategoryDef {
  id: string;
  label: string;
  color: string;
  items: string[];
}

const CATEGORIES: CategoryDef[] = [
  { id: 'hunting', label: 'Hunting', color: '#28A745', items: [
    'Bolt-Action Rifle', 'Compound Bow Set', 'Tree Stand', 'Ground Blind', 'Game Calls', 'Trail Camera', 'Binoculars', 'Hunting Knife Kit', 'Game Processing Kit', 'Scent Control Kit',
  ]},
  { id: 'fishing', label: 'Fishing', color: '#007BFF', items: [
    'Spinning Rod & Reel', 'Baitcasting Rod & Reel', 'Fly Fishing Rod Set', 'Tackle Box', 'Fish Finder', 'Landing Net', 'Fishing Vest', 'Waders', 'Ice Fishing Auger', 'Live Bait Container',
  ]},
  { id: 'boating', label: 'Boating', color: '#17A2B8', items: [
    'Bass Boat', 'Canoe', 'Kayak', 'Jon Boat', 'Electric Trolling Motor', 'Life Jacket Set', 'Anchor Kit', 'Boat Battery Charger', 'Paddle Set', 'Boat Cover/Trailer Lock',
  ]},
  { id: 'atving', label: 'ATVing', color: '#FF6B35', items: [
    'ATV (4x4)', 'UTV/Side-by-Side', 'Helmet Set', 'Winch Kit', 'Tire Repair Kit', 'Cargo Rack/Box', 'GPS Mount', 'Mud Tires', 'Fuel Can', 'Tow Strap',
  ]},
  { id: 'camping', label: 'Camping', color: '#FFC107', items: [
    'Tent (4-person)', 'Sleeping Bag', 'Camp Stove', 'Cooler (bear-proof)', 'Lantern (LED)', 'Camp Chair Set', 'Hammock', 'Tarp Shelter', 'Water Filter', 'First Aid Kit',
  ]},
  { id: 'bushcraft', label: 'Bushcraft', color: '#6F42C1', items: [
    'Axe/Hatchet', 'Fixed-Blade Knife', 'Fire Starter Kit', 'Paracord Bundle', 'Shelter Building Kit', 'Foraging Guide', 'Compass', 'Multi-Tool', 'Water Purification Tablets', 'Snare/Trap Kit',
  ]},
  { id: 'biking', label: 'Biking', color: '#E83E8C', items: [
    'Mountain Bike', 'Hybrid Bike', 'Helmet', 'Bike Repair Kit', 'Pannier Bags', 'Bike Lock', 'Hydration Pack', 'Trail Bell', 'Spare Tube Set', 'Bike Rack',
  ]},
  { id: 'hiking_geocaching', label: 'Hiking / GeoCaching', color: '#20C997', items: [
    'Hiking Boots', 'Backpack (30L)', 'Trekking Poles', 'Headlamp', 'GPS Device', 'GeoCache Kit', 'Binoculars (compact)', 'Rain Jacket', 'Trail Map Holder', 'Whistle/Signal Mirror',
  ]},
  { id: 'other', label: 'Other', color: '#888888', items: [
    'Snowshoes', 'Cross-Country Skis', 'Ice Cleats', 'Generator', 'Chainsaw', 'Ladder', 'Cooler Cart', 'Bug Net', 'Solar Charger', 'Wildlife Camera Trap',
  ]},
];

const CATEGORY_MAP = new window.Map(CATEGORIES.map((c) => [c.id, c]));

const CONDITIONS = [
  { id: 'excellent', label: 'Excellent' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' },
  { id: 'needs_repair', label: 'Needs Repair' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EquipmentItem {
  id: string;
  name: string;
  category: string;
  itemType: string | null;
  description: string | null;
  condition: string;
  lastInspected: string | null;
  dockName: string | null;
  locationLat: string | null;
  locationLon: string | null;
  isMappable: boolean;
  usageCount: number;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'confirm'; lat: number; lng: number }
  | { type: 'create'; lat?: number; lng?: number }
  | { type: 'edit'; item: EquipmentItem }
  | { type: 'view'; item: EquipmentItem };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EquipmentPage() {
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('hunting');
  const [formItemType, setFormItemType] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCondition, setFormCondition] = useState('good');
  const [formDockName, setFormDockName] = useState('');
  const [formLat, setFormLat] = useState('');
  const [formLon, setFormLon] = useState('');

  useEffect(() => { loadEquipment(); }, []);

  async function loadEquipment() {
    setLoading(true);
    const res = await apiFetch<EquipmentItem[]>('/api/admin/equipment');
    if (res.success && res.data) setItems(res.data);
    setLoading(false);
  }

  const catDef = CATEGORY_MAP.get(formCategory);
  const itemTypeOptions = catDef?.items || [];

  // --- Map click: place pin ---
  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'none' && panel.type !== 'view') return;
    if (!e.detail.latLng) return;
    setPanel({ type: 'confirm', lat: e.detail.latLng.lat, lng: e.detail.latLng.lng });
  }, [panel.type]);

  // --- Confirm pin -> open create form ---
  function confirmPin() {
    if (panel.type !== 'confirm') return;
    resetForm();
    setFormLat(panel.lat.toFixed(7));
    setFormLon(panel.lng.toFixed(7));
    setPanel({ type: 'create', lat: panel.lat, lng: panel.lng });
  }

  // --- View item ---
  function viewItem(item: EquipmentItem) {
    setPanel({ type: 'view', item });
  }

  // --- Edit item ---
  function openEdit(item: EquipmentItem) {
    setFormName(item.name);
    setFormCategory(item.category);
    setFormItemType(item.itemType || '');
    setFormDescription(item.description || '');
    setFormCondition(item.condition);
    setFormDockName(item.dockName || '');
    setFormLat(item.locationLat || '');
    setFormLon(item.locationLon || '');
    setPanel({ type: 'edit', item });
  }

  function resetForm() {
    setFormName('');
    setFormCategory('hunting');
    setFormItemType('');
    setFormDescription('');
    setFormCondition('good');
    setFormDockName('');
    setFormLat('');
    setFormLon('');
  }

  // --- Save ---
  async function saveEquipment() {
    const payload = {
      name: formName,
      category: formCategory,
      item_type: formItemType || undefined,
      description: formDescription || undefined,
      condition: formCondition,
      dock_name: formDockName || undefined,
      location_lat: formLat ? parseFloat(formLat) : undefined,
      location_lon: formLon ? parseFloat(formLon) : undefined,
    };

    if (panel.type === 'edit') {
      await apiFetch(`/api/admin/equipment/${panel.item.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    } else {
      await apiFetch('/api/admin/equipment', { method: 'POST', body: JSON.stringify(payload) });
    }
    setPanel({ type: 'none' });
    loadEquipment();
  }

  // --- Delete ---
  async function deleteEquipment(item: EquipmentItem) {
    if (!confirm(`Delete "${item.name}"?`)) return;
    await apiFetch(`/api/admin/equipment/${item.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadEquipment();
  }

  const mappedItems = items.filter((i) => i.locationLat && i.locationLon);

  return (
    <div style={s.page}>
      {/* LEFT: Full map */}
      <div style={s.mapCol}>
        <div style={s.mapHeader}>
          <h1 style={s.pageTitle}>Equipment</h1>
          {panel.type === 'none' && (
            <span style={s.mapHint}>Click the map to place new equipment</span>
          )}
        </div>
        <div style={{ ...s.mapWrap, position: 'relative' }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              defaultCenter={PROPERTY_CENTER}
              defaultZoom={15}
              mapTypeId="satellite"
              gestureHandling="greedy"
              fullscreenControl={false}
              mapId="grimsthorpe-admin-equip"
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
            >
              <PropertyOverlay />
              {/* Equipment markers */}
              {mappedItems.map((item) => {
                const isEditing = panel.type === 'edit' && panel.item.id === item.id;
                const lat = isEditing && formLat ? parseFloat(formLat) : parseFloat(item.locationLat!);
                const lng = isEditing && formLon ? parseFloat(formLon) : parseFloat(item.locationLon!);
                return (
                  <AdvancedMarker
                    key={item.id}
                    position={{ lat, lng }}
                    title={item.name}
                    draggable={isEditing}
                    onClick={() => { if (!isEditing) viewItem(item); }}
                    onDragEnd={(e) => {
                      if (isEditing && e.latLng) {
                        setFormLat(e.latLng.lat().toFixed(7));
                        setFormLon(e.latLng.lng().toFixed(7));
                      }
                    }}
                  >
                    <Pin
                      background={CATEGORY_MAP.get(item.category)?.color || '#888'}
                      borderColor={isEditing ? '#00BFFF' : '#000'}
                      glyphColor="#FFF"
                      scale={0.8}
                    />
                  </AdvancedMarker>
                );
              })}

              {/* Pending pin */}
              {panel.type === 'confirm' && (
                <AdvancedMarker
                  position={{ lat: panel.lat, lng: panel.lng }}
                  draggable
                  onDragEnd={(e) => { if (e.latLng) setPanel({ type: 'confirm', lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                >
                  <Pin background="#FFC107" borderColor="#FF8F00" glyphColor="#000" />
                </AdvancedMarker>
              )}

              {/* Info window */}
              {panel.type === 'view' && panel.item.locationLat && (
                <InfoWindow
                  position={{ lat: parseFloat(panel.item.locationLat!), lng: parseFloat(panel.item.locationLon!) }}
                  onCloseClick={() => setPanel({ type: 'none' })}
                  pixelOffset={[0, -40]}
                >
                  <div style={{ color: '#000', minWidth: 160 }}>
                    <strong style={{ fontSize: 14 }}>{panel.item.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>
                      {CATEGORY_MAP.get(panel.item.category)?.label} &middot; {panel.item.condition?.replace('_', ' ')}
                      {panel.item.itemType ? ` · ${panel.item.itemType}` : ''}
                    </p>
                    <button onClick={() => openEdit(panel.item)}
                      style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Edit Equipment
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </APIProvider>
        </div>
      </div>

      {/* RIGHT: Panel */}
      <div style={s.panelCol}>
        <div style={s.panelContent}>
          {/* Default: equipment list */}
          {panel.type === 'none' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={s.panelTitle}>Inventory</h3>
                <button style={s.btnPrimary} onClick={() => { resetForm(); setPanel({ type: 'create' }); }}>+ Add</button>
              </div>

              <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 12 }}>
                {items.length} item{items.length !== 1 ? 's' : ''} &middot; {mappedItems.length} on map. Click map to place or click an item below.
              </p>

              {/* Category groups */}
              {CATEGORIES.filter((c) => items.some((i) => i.category === c.id)).map((cat) => {
                const catItems = items.filter((i) => i.category === cat.id);
                return (
                  <div key={cat.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: cat.color }} />
                      <span style={{ color: COLORS.textPrimary, fontSize: 12, fontWeight: 600 }}>{cat.label}</span>
                      <span style={{ color: COLORS.textMuted, fontSize: 11 }}>({catItems.length})</span>
                    </div>
                    {catItems.map((item) => (
                      <div key={item.id} style={s.itemRow} onClick={() => item.locationLat ? viewItem(item) : openEdit(item)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: COLORS.textPrimary, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                          <div style={{ color: COLORS.textMuted, fontSize: 10 }}>
                            {item.itemType || '--'} &middot; {item.condition?.replace('_', ' ')}
                            {item.locationLat ? '' : ' · no location'}
                          </div>
                        </div>
                        <ConditionDot condition={item.condition} />
                      </div>
                    ))}
                  </div>
                );
              })}

              {items.length === 0 && !loading && (
                <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>No equipment yet. Click the map or "+ Add" to get started.</p>
              )}
            </>
          )}

          {/* Confirm pin */}
          {panel.type === 'confirm' && (
            <>
              <h3 style={s.panelTitle}>Confirm Location</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>Drag the yellow pin to adjust.</p>
              <p style={{ color: COLORS.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>{panel.lat.toFixed(6)}, {panel.lng.toFixed(6)}</p>
              <div style={s.panelActions}>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={confirmPin}>Confirm & Create</button>
              </div>
            </>
          )}

          {/* View item */}
          {panel.type === 'view' && (
            <>
              <h3 style={s.panelTitle}>{panel.item.name}</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <CategoryBadge category={panel.item.category} />
                <ConditionBadge condition={panel.item.condition} />
              </div>
              {panel.item.itemType && <p style={{ color: COLORS.textSecondary, fontSize: 12, marginBottom: 4 }}>Type: {panel.item.itemType}</p>}
              {panel.item.description && <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 4 }}>{panel.item.description}</p>}
              {panel.item.dockName && <p style={{ color: COLORS.textMuted, fontSize: 12 }}>Dock: {panel.item.dockName}</p>}
              {panel.item.locationLat && (
                <p style={{ color: COLORS.textSecondary, fontSize: 11, fontFamily: 'monospace', marginTop: 8 }}>
                  {panel.item.locationLat}, {panel.item.locationLon}
                </p>
              )}
              <div style={s.panelActions}>
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteEquipment(panel.item)}>Delete</button>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Close</button>
                <button style={s.btnPrimary} onClick={() => openEdit(panel.item)}>Edit</button>
              </div>
            </>
          )}

          {/* Create / Edit form */}
          {(panel.type === 'create' || panel.type === 'edit') && (
            <>
              <h3 style={s.panelTitle}>{panel.type === 'edit' ? 'Edit Equipment' : 'New Equipment'}</h3>

              <div style={s.field}>
                <label style={s.label}>Category</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => { setFormCategory(c.id); setFormItemType(''); }}
                      style={{ padding: '3px 8px', borderRadius: 10, border: formCategory === c.id ? `2px solid ${c.color}` : `1px solid ${COLORS.borderDefault}`,
                        backgroundColor: formCategory === c.id ? `${c.color}22` : 'transparent', color: formCategory === c.id ? c.color : COLORS.textMuted, fontSize: 10, cursor: 'pointer', fontWeight: 500 }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Item Type</label>
                <select value={formItemType} onChange={(e) => {
                  setFormItemType(e.target.value);
                  if (e.target.value && !formName) setFormName(e.target.value);
                }} style={s.input}>
                  <option value="">-- Select or custom --</option>
                  {itemTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} placeholder="e.g. My 14ft Alumacraft" />
              </div>

              <div style={s.field}>
                <label style={s.label}>Description</label>
                <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} style={{ ...s.input, resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Condition</label>
                  <select value={formCondition} onChange={(e) => setFormCondition(e.target.value)} style={s.input}>
                    {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Dock / Storage</label>
                  <input value={formDockName} onChange={(e) => setFormDockName(e.target.value)} placeholder="Optional" style={s.input} />
                </div>
              </div>

              {(formLat || formLon) && (
                <div style={s.field}>
                  <label style={s.label}>Location</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: COLORS.textSecondary, fontSize: 11, fontFamily: 'monospace' }}>{formLat}, {formLon}</span>
                    <button style={s.btnSmallDanger} onClick={() => { setFormLat(''); setFormLon(''); }}>Clear</button>
                  </div>
                </div>
              )}

              <div style={s.panelActions}>
                {panel.type === 'edit' && (
                  <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteEquipment(panel.item)}>Delete</button>
                )}
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={saveEquipment} disabled={!formName.trim()}>
                  {panel.type === 'edit' ? 'Update' : 'Create'}
                </button>
              </div>
            </>
          )}

          {loading && <div style={{ color: COLORS.textMuted, padding: 24 }}>Loading...</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ConditionBadge({ condition }: { condition: string }) {
  const colorMap: Record<string, string> = { excellent: '#28A745', good: '#28A745', fair: '#FFC107', poor: '#DC3545', needs_repair: '#DC3545' };
  return <span style={{ ...s.badge, backgroundColor: `${colorMap[condition] || '#888'}22`, color: colorMap[condition] || '#888' }}>{condition.replace('_', ' ')}</span>;
}

function CategoryBadge({ category }: { category: string }) {
  const cat = CATEGORY_MAP.get(category);
  return <span style={{ ...s.badge, backgroundColor: `${cat?.color || '#888'}22`, color: cat?.color || '#888' }}>{cat?.label || category}</span>;
}

function ConditionDot({ condition }: { condition: string }) {
  const colorMap: Record<string, string> = { excellent: '#28A745', good: '#28A745', fair: '#FFC107', poor: '#DC3545', needs_repair: '#DC3545' };
  return <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorMap[condition] || '#888', flexShrink: 0 }} />;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  mapHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', flexShrink: 0 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  mapHint: { fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic' },
  mapWrap: { flex: 1, borderRadius: 8, overflow: 'hidden', margin: '0 0 0 16px' },
  panelCol: { width: 380, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  itemRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 2, borderRadius: 4, cursor: 'pointer', border: `1px solid transparent` },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmallDanger: { padding: '3px 8px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 4, fontSize: 10, cursor: 'pointer' },
};
